<template>
  <b-card class="shadow-sm company-taggings" header-bg-variant="" header-text-variant="black">
    <template v-slot:header>
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-baseline gap-4 flex-wrap">
          <h5 class="mb-0">{{ finalTitle }}</h5>
        </div>
        <div>
          <b-button v-if="isEditMode" key="save" size="sm" variant="success" class="mr-2" @click="onSave"> {{ $t('buttons.save') }} </b-button>
          <b-button v-if="!isEditMode && hasAnySelectable" key="edit" size="sm" variant="sharp-primary" @click="onEdit">
            <b-icon icon="pencil" class="mr-2"></b-icon>
            {{ $t('vendorTaggings.edit') }}
          </b-button>
          <b-button v-else-if="isEditMode" key="cancel" size="sm" variant="secondary" @click="onEditCancel"> {{ $t('buttons.cancel') }} </b-button>
        </div>
      </div>
    </template>
    <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
    <div class="d-flex flex-wrap mt-2 mb-2">
      <div v-if="!loading && !hasCategories" class="w-100">
        <span class="text-uppercase text-muted font-weight-500">{{ $t('vendorTaggings.noCategories') }}</span>
      </div>
      <div v-for="(cat, idx) in categoriesOrdered" :key="cat.id" class="w-100">
        <div v-if="isEditMode || cat.selected.length || showEmptyCategoriesDisplayMode" class="">  <!-- hides categories without selections in display mode, unless we have override prop set -->
          <hr v-if="idx !== 0" class="my-3" />
          <div class="d-flex align-items-center justify-content-between">
            <div class="">
              <div class="d-flex gap-4 flex-wrap align-items-center">
                <div class="text-primary-darker font-weight-bold font-size-100">{{ cat.label }}</div>
                <div v-if="isEditMode && cat.isLocked" class="text-muted special-text">{{ $t('vendorTaggings.noEditsAllowed') }}</div>
              </div>
              <div class="text-muted small mb-2 text-show-breaks">{{ cat.description }}</div>
            </div>
            <div class="">
              <div class="text-muted special-text font-size-70">{{ cat.externalStatusI18nKey ? $t(cat.externalStatusI18nKey) : cat.external_status_text }}</div>
            </div>
          </div>

          <!-- <div class="d-flex flex-wrap gap-2 company-tag-category-options"> -->
          <div class="company-tag-category-options w-100 d-flex flex-wrap">
            <!-- tried style="columns: 4;" but got funky when toggling edit on/off -->
            <div v-for="opt in isEditMode ? cat.options : cat.options.filter((o) => localSelectedTagIds.includes(o.value))" :key="opt.id" class="mb-1 w-25">
              <div :class="[cat.selected.includes(opt.value) ? 'is-checked' : 'not-checked', isEditMode && !cat.isLocked ? 'editable' : 'not-editable']" class="field-checkbox pl-2 d-inline-flex align-items-center company-tag-option">
                <Checkbox v-if="isEditMode && !cat.isLocked" key="editable" :id="opt.value" name="vendor_contacts" :value="opt.value" v-model="cat.selected" @input="handleSelection" :disabled="cat.isLocked || !isEditMode" />
                <b-icon v-else key="notEditable" :icon="localSelectedTagIds.includes(opt.value) ? 'check-circle-fill' : 'x-circle'" :variant="localSelectedTagIds.includes(opt.value) ? 'primary' : 'secondary-100'" style="height: 20px; width: 20px"></b-icon>
                <label :for="opt.value" class="mb-0 px-2 d-flex align-items-center" style="padding-top: 6px; padding-bottom: 6px">
                  <featured-vendor-tag v-if="opt.tag && opt.tag.is_featured" :tag="opt.tag" class="font-size-80 text-uppercase font-weight-500"></featured-vendor-tag>
                  <span v-else>{{ opt.label }}</span>
                </label>
              </div>
            </div>
          </div>
          <div class="d-flex gap-4 align-items-baseline mt-1 ml-3">
            <div v-if="!isEditMode && cat.selected.length < 1" class="text-muted special-text">{{ $t('vendorTaggings.noneSelected') }}</div>
            <div class="text-muted small">{{ $t('vendorTaggings.unselectedOptions', { count: cat.options.length - cat.selected.length }) }}</div>
          </div>
        </div>
      </div>
      <div v-if="!isEditMode && !showEmptyCategoriesDisplayMode && categoryNamesWithoutSelections.length" class="w-100">
        <hr class="my-3" />
        <div class="text-muted small mt-2 d-flex gap-2">
          <div class="">{{ $t('vendorTaggings.noSelectionsFor') }}</div>
          <div class="d-flex flex-wrap gap-3">
            <div v-for="(catName, idx) in categoryNamesWithoutSelections" :key="`${catName}_${idx}`" class="font-weight-500 text-primary-darker">{{ catName }}</div>
          </div>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
  import { suppliosApi } from '../../../js/api-service';
  import { errorHandling, currentUserInfoMixin } from '../../../js/custom-vue-mixins';
  //import cloneDeep from 'lodash/cloneDeep';
  import isPlainObject from 'lodash/isPlainObject';
  import sortBy from 'lodash/sortBy';
  import Checkbox from 'primevue/checkbox';
  import FeaturedVendorTag from './FeaturedVendorTag.vue';

  class CompanyTagCategory {
    constructor({
      // pull out the ones we know we want to work with, set defaults for, etc.
      id,
      name,
      slug,
      options, // ARRAY of options (object also possible, but less desired... we want extra stuff for each option)
      selected, // ARRAY of ALREADY SELECTED OPTION VALUES/IDs, if any - can be a superset of the options here... we ignore any that are not in our options
      description, // pick internal/external based on currentUserInfo, set as 'description'
      position, // sort order of this category for display
      is_internal,
      can_tu_select, // based on the CURRENT TU in backend - use this to know if we should lock
      can_external_select,
      can_external_view,
      ...params // other params
    } = {}) {
      // NOTE - some fields are missing on EXTERNAL SIDE
      this.id = id;
      this.name = name; // the LABEL
      this.label = name; // alias to name
      this.slug = slug; // what we use as 'name' in the form
      this.description = description ?? '';
      this.position = position ?? 0; // params?.position ?? 0;
      this.isLocked = !can_tu_select;
      this.can_external_select = can_external_select; // boolean, likely only shows up in INTERNAL schema  (but backend takes care of logic)
      this.can_external_view = can_external_view; // boolean, likely only shows up in INTERNAL schema (but backend takes care of logic)
      // we'll make an 'external_status_text' to show to INTERNAL people about whether this category can be view/edit by vendors
      this.external_status_text = ''; // default..
      this.externalStatusI18nKey = ''; // no `this.$t` here (plain class) - keep English fallback text + i18n key, translate at render time
      if (is_internal) {
        if (can_external_select) {
          this.external_status_text = 'Vendors Can Edit';
          this.externalStatusI18nKey = 'vendorTaggings.vendorsCanEdit';
        } else if (can_external_view) {
          this.external_status_text = 'Vendors Can View';
          this.externalStatusI18nKey = 'vendorTaggings.vendorsCanView';
        } else {
          this.external_status_text = 'Internal Only';
          this.externalStatusI18nKey = 'vendorTaggings.internalOnly';
        }
      } // if not internal, don't show anything, leave empty string
      // STASH EXTRA params
      Object.entries(params).forEach(([paramKey, paramVal]) => {
        this[paramKey] = paramVal;
      });
      // OPTIONS - we'll always make an ARRAY of OBJECTS with {value, label}
      if (!!options && Array.isArray(options)) {
        if (!options.length) {
          this.options = [];
        }
        const firstOption = options[0];
        if (typeof firstOption === 'string') {
          // we have array of strings... make into array of objects
          this.options = options.map((opt) => ({ value: opt, label: opt })); // could use startCase if needed...
        } else if (isPlainObject(firstOption)) {
          // check if we have to re-key the objects
          const valueKey = 'value' in firstOption ? 'value' : 'id' in firstOption ? 'id' : null; // if we have null here, we'll key by index
          const labelKey = 'label' in firstOption ? 'label' : 'name' in firstOption ? 'name' : null; // if we have null here, we don't know what the hell to do....
          if (!labelKey) {
            throw new Error('Unknown label key for making array of options for company tags');
          }
          this.options = options.map((opt) => {
            return { ...opt, value: opt[valueKey], label: opt[labelKey] };
          });
        } else {
          throw new Error('Unknown option type when making array of options for CompanyTags');
        }
      } else if (!!options && isPlainObject(options)) {
        this.options = Object.entries(options).map(([value, label]) => ({ value, label }));
      } else {
        this.options = [];
      }
      // WE WILL SORT THE OPTIONS - using 'label'
      this.options = sortBy(this.options, ['label']);
      // HANDLE PRE-SELECTED
      // const allOptionValues = this.options.map(opt => opt.value);
      // const preSelected = !!selected && Array.isArray(selected) ? selected : [];
      // this.selected = allOptionValues.filter(val => preSelected.includes(val));
      this.setSelected(selected);
    }
    setSelected(valuesArray) {
      // call this if you want to SET this.selected
      // will make sure the values are in possible options
      // this wipes out anything else in this.selected
      // use this if we already have these built, and getting a new set of selections from API or somewhere
      const allOptionValues = this.options.map((opt) => opt.value);
      const selectedValues = !!valuesArray && Array.isArray(valuesArray) ? valuesArray : [];
      this.selected = allOptionValues.filter((val) => selectedValues.includes(val));
    }
    getSelected() {
      // returns the selected VALUES (not full objects)
      return [...this.selected];
    }
  }

  export default {
    name: 'VendorTaggings',
    components: {
      Checkbox,
      FeaturedVendorTag,
    },
    mixins: [errorHandling, currentUserInfoMixin],
    i18n: {
      messages: {
        en: {
          vendorTaggings: {
            title: 'Categories & Capabilities',
            edit: 'Edit',
            noCategories: 'No categories to show',
            noEditsAllowed: 'No Edits Allowed',
            noneSelected: 'None Selected',
            unselectedOptions: '{count} unselected options',
            noSelectionsFor: 'No selections for:',
            vendorsCanEdit: 'Vendors Can Edit',
            vendorsCanView: 'Vendors Can View',
            internalOnly: 'Internal Only',
            fetchTagsError: 'Problem fetching Vendor Tags',
            savedToast: 'Saved',
            saveError: 'There was a problem saving the updates',
          },
        },
        es: {
          vendorTaggings: {
            title: 'Categorías y Capacidades',
            edit: 'Editar',
            noCategories: 'No hay categorías para mostrar',
            noEditsAllowed: 'Edición No Permitida',
            noneSelected: 'Ninguna Seleccionada',
            unselectedOptions: '{count} opciones sin seleccionar',
            noSelectionsFor: 'Sin selecciones para:',
            vendorsCanEdit: 'Los Proveedores Pueden Editar',
            vendorsCanView: 'Los Proveedores Pueden Ver',
            internalOnly: 'Solo Interno',
            fetchTagsError: 'Problema al obtener las Etiquetas de Proveedor',
            savedToast: 'Guardado',
            saveError: 'Hubo un problema al guardar los cambios',
          },
        },
        pt: {
          vendorTaggings: {
            title: 'Categorias e Capacidades',
            edit: 'Editar',
            noCategories: 'Nenhuma categoria para mostrar',
            noEditsAllowed: 'Edição Não Permitida',
            noneSelected: 'Nenhuma Selecionada',
            unselectedOptions: '{count} opções não selecionadas',
            noSelectionsFor: 'Sem seleções para:',
            vendorsCanEdit: 'Fornecedores Podem Editar',
            vendorsCanView: 'Fornecedores Podem Visualizar',
            internalOnly: 'Apenas Interno',
            fetchTagsError: 'Problema ao buscar as Etiquetas de Fornecedor',
            savedToast: 'Salvo',
            saveError: 'Ocorreu um problema ao salvar as alterações',
          },
        },
        'zh-Hans': {
          vendorTaggings: {
            title: '类别和能力',
            edit: '编辑',
            noCategories: '没有可显示的类别',
            noEditsAllowed: '不允许编辑',
            noneSelected: '未选择',
            unselectedOptions: '{count} 个未选选项',
            noSelectionsFor: '以下类别未选择：',
            vendorsCanEdit: '供应商可编辑',
            vendorsCanView: '供应商可查看',
            internalOnly: '仅限内部',
            fetchTagsError: '获取供应商标签时出现问题',
            savedToast: '已保存',
            saveError: '保存更新时出现问题',
          },
        },
        'zh-Hant': {
          vendorTaggings: {
            title: '類別和能力',
            edit: '編輯',
            noCategories: '沒有可顯示的類別',
            noEditsAllowed: '不允許編輯',
            noneSelected: '未選取',
            unselectedOptions: '{count} 個未選取選項',
            noSelectionsFor: '以下類別未選取：',
            vendorsCanEdit: '供應商可編輯',
            vendorsCanView: '供應商可檢視',
            internalOnly: '僅限內部',
            fetchTagsError: '取得供應商標籤時發生問題',
            savedToast: '已儲存',
            saveError: '儲存更新時發生問題',
          },
        },
      },
    },
    props: {
      companyId: {
        type: String,
        required: true,
      },
      selectedTags: {
        // the SELECTED tag OBJECTS - initial value
        // array of objects - tags that are selected for this company (has tagging linking company <-> tag)
        // as of Aug 13 2022 - we are NOT updating local data when this prop changes
        // because likely this is the component that will make all updates, and we can manage local data in here
        // we get array of IDs from here and stash on local data in created()
        // actually... we should now be reactive to changes here...
        type: Array,
        required: false,
      },
      title: {
        // prop overrides; when empty we fall back to the translated default (see finalTitle)
        type: String,
        default: '',
      },
      showEmptyCategoriesDisplayMode: {
        // show categories even if they have no options in display (not edit) mode -- that was the old behavior
        // we changed default to hide empty categories in display mode, but you can set this prop to true to keep old behavior
        type: Boolean,
        default: false,
      },
    },
    data: function () {
      return {
        errorMsg: 'Error',
        error: false,
        loading: false,
        saving: false,
        isEditMode: false,
        isSuccess: false,
        categories: [], // CompanyTagCategory instances, with options (available tags) in each
        tagsById: {}, // flat object of ALL TAG OBJECTS, keyed by their ID - used to emit full tag objects back up on updates
        localSelectedTagIds: [], // we set initial value in created() then update it when local selections change
        lastSavedSelectedTagIds: [], // stash here when starting edits
        hasAnySelectable: false, // set true after going through categories, if any have can_tu_select
      };
    },
    computed: {
      finalTitle() {
        return this.title || this.$t('vendorTaggings.title');
      },
      hasCategories() {
        return !!this.categories && Array.isArray(this.categories) && this.categories.length;
      },
      categoriesOrdered() {
        const cats = !!this.categories && Array.isArray(this.categories) ? this.categories : [];
        const hasPosition = !!cats.length && 'position' in cats[0];
        return hasPosition ? sortBy(cats, ['position']) : cats;
      },
      isInternal() {
        return this.$currentUserInfo?.is_internal;
      },
      isExternal() {
        return this.$currentUserInfo?.is_external;
      },
      categoryNamesWithoutSelections() {
        // Array of names of categories that have no selected options -- we show this when now in edit mode and not showing empty categories
        return Array.isArray(this.categories) ? this.categories.filter((cat) => cat.selected.length < 1).map(c => c.name) : [];
      },
    },
    watch: {
      selectedTags: function (selectedTags) {
        // since this is an array, we can't tell difference between newVal and oldVal (most likely)
        // so we'll compute the new value array, then compare it to current local copy
        // basically the same thing we do on created()
        if (!!selectedTags && Array.isArray(selectedTags)) {
          const tagIds = selectedTags.map((t) => t.id);
          // check if they're different???
          this.setLocalSelections(tagIds);
        }
      },
    },
    created() {
      // set this.localSelectedTagIds from initial prop value
      // initial prop value should be array of objects
      if (!!this.selectedTags && Array.isArray(this.selectedTags)) {
        const tagIds = this.selectedTags.map((t) => t.id);
        this.setLocalSelections(tagIds);
      } else {
        this.localSelectedTagIds = [];
      }
    },
    mounted() {
      this.getAllTags();
    },
    methods: {
      // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
      async getAllTags() {
        // fetch all the tags with categories (not taggings)
        this.loading = true;
        let tagList;
        try {
          const resp = await suppliosApi.companyTag.list();
          tagList = resp?.data ?? []; // not paginated... you'd miss them
        } catch (err) {
          this.apiErrorHandler(err, this.$t('vendorTaggings.fetchTagsError'));
        }
        this.buildTagCategories(tagList);
        this.loading = false;
      },
      buildTagCategories(tagList) {
        // process the tags, split out categories and build options
        const tagCatsById = {}; // push here as we loop through tags, then make fields for schema from this
        const tagsByCatId = {}; // lists of tags, keyed by category ID
        const tagsById = {}; // object of all tag objects, keyed by their ID - will set on component data

        tagList.forEach((tag) => {
          //console.log('processing tag', tag);
          tagsById[tag.id] = tag;
          const cat = tag.category;
          const catId = cat.id;
          if (!(catId in tagCatsById)) {
            // add the category to stash
            tagCatsById[catId] = cat;
          }
          if (!(catId in tagsByCatId)) {
            tagsByCatId[catId] = []; // setup empty array first, then push in
          }
          tagsByCatId[catId].push(tag);
        });
        console.log('tagCatsById', tagCatsById);
        console.log('tagsByCatId', tagsByCatId);
        // now make array of category instances with options in each one
        const categories = [];
        const selected = [...this.localSelectedTagIds]; // might be empty...
        const { is_internal, is_external } = this.$currentUserInfo; // we'll pass these in so they are contained in the class instance
        Object.values(tagCatsById).forEach((tagCat) => {
          // options are either object { value: 'Label' } or [{value, label}, {value, label}, ... ]
          const options = tagsByCatId[tagCat.id].map((t) => ({ value: t.id, label: t.name, tag: t }));
          //const { id, name, slug, description, can_tu_select, ...params } = tagCat;
          /* categories.push(new CompanyTagCategory({
                    id: id,
                    name: name,
                    slug: slug,
                    description: description,
                    isLocked: !can_tu_select,
                    options: [...options],
                    selected: [...selected],  // might be empty for now
                    ...params
                })); */
          categories.push(
            new CompanyTagCategory({
              ...tagCat,
              is_internal,
              is_external,
              options: [...options],
              selected: [...selected], // might be empty for now
            })
          );
        });
        this.categories = categories;
        this.tagsById = tagsById;
        this.hasAnySelectable = this.categories.some((cat) => !cat.isLocked);
      },
      handleSelection() {
        // called from @input of each checkbox
        // go through all CompanyTagCategory instances and get current selections, then set on local data
        const allSelected = []; // array of tag IDs
        this.categories.forEach((cat) => {
          allSelected.push(...cat.getSelected());
        });
        this.localSelectedTagIds = allSelected;
        // WAIT UNTIL WE SAVE this.emitUpdate();
      },
      setLocalSelections(selectedIds) {
        // pass in array of selected IDs, like from backend update
        // we use this in created() hook AND when we get updated data from backend or from prop
        // so it must be able to handle having categories OR NOT having categories (yet)
        // if we have categories, we'll update things "through" the categories/checkboxes... set them, then get them
        // if we DO NOT have categories, we'll just set this.localSelectedTagIds instead
        if (this.catgories && Array.isArray(this.categories) && this.categories.length) {
          // we HAVE categories - update through those
          // this runs through them and sets the selections on each
          // first set the internal selections in each category, then we'll call normal method to collect from each, just to be consistent
          this.categories.forEach((cat) => {
            cat.setSelected([...selectedIds]);
          });
          this.handleSelection(); // gather all the selections from each category (which you just set) and set on local selection IDs data
        } else {
          // no categories yet... just set this.localSelectedTagIds
          this.localSelectedTagIds = [...selectedIds];
        }
      },
      emitUpdate() {
        // emit 'update' event to parent
        // must emit the full tag objects (selected ones) - NOT ONLY just the values array
        // must put it in same structure as 'resource' (company/vendor)
        const selectedIds = [...this.localSelectedTagIds];
        const selectedTagObjects = [];
        selectedIds.forEach((tagId) => {
          selectedTagObjects.push(this.tagsById[tagId]);
        });
        const updatedResource = {
          company_tags: selectedTagObjects, // make sure this matches what your schema expects!
          //company_tags_objects: selectedTagObjects,  // full objects here
          //company_tags: [...selectedIds],  // just IDs here
        };
        this.$emit('update', updatedResource);
      },
      showSuccess() {
        // do something to show success... toast?
        this.$toast.success(this.$t('vendorTaggings.savedToast'));
      },
      onEdit() {
        // start edit process - separate 'modified' data from 'original' data
        this.lastSavedSelectedTagIds = [...this.localSelectedTagIds];
        this.isEditMode = true;
      },
      onEditCancel() {
        // only when cancelled without saving
        this.localSelectedTagIds = [...this.lastSavedSelectedTagIds];
        this.reset();
      },
      reset() {
        this.lastSavedSelectedTagIds = [];
        this.isEditMode = false;
      },
      async onSave() {
        this.saving = true;
        let resp;
        try {
          // collect tags by category - what the endpoint expects - {categoryId: [tagIds]}
          const tagData = {};
          this.categories.forEach((cat) => {
            tagData[cat.id] = [...cat.getSelected()];
          });
          resp = await suppliosApi.vendorStandard.update_company_tags(this.companyId, tagData);
        } catch (err) {
          this.apiErrorHandler(err, this.$t('vendorTaggings.saveError'));
        }
        if (resp?.data) {
          const allTagObjects = resp.data; // returns a list/array of serialized CompanyTag objects
          const allTagIds = allTagObjects.map((t) => t.id);
          this.setLocalSelections(allTagIds);
          this.saving = false;
          this.showSuccess();
          this.emitUpdate();
          this.reset();
        } else {
          this.saving = false; // set anyways
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .company-taggings {
    .company-tag-option {
      background-color: transparent;
      border-radius: 0.25rem;
      border-width: 1px;
      border-style: solid;
      border-color: transparent;
      transition: 0.15s all;
      &.editable {
        background-color: $gray-50;
        border-color: $gray-100;
      }
      &:hover.editable {
        background-color: $gray-150;
        border-color: $gray-200;
      }
      &.is-checked.editable {
        background-color: $gray-100;
        border-color: $gray-200;
        &:hover {
          background-color: $gray-200;
          border-color: $gray-300;
        }
      }
      cursor: pointer;
      label {
        cursor: pointer;
      }
      &.not-editable {
        cursor: default;
        label {
          cursor: default;
        }
        &.not-checked label {
          //color: var(--gray-400);
          opacity: 0.5;
        }
      }
    }
  }
</style>
